import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../constants/property';

/** Map API role row to { id, name } */
const normalizeRoleItem = (item) => {
  if (!item) return null;
  const name = item.roleName || item.name || item.roleCode;
  if (!name) return null;
  const id = item.id ?? item.roleId;
  return { id: id != null ? id : undefined, name: String(name) };
};

const UserSettings = () => {
  const [roleItems, setRoleItems] = useState([]);
  const [roleNameInput, setRoleNameInput] = useState('');
  const [roleCodeInput, setRoleCodeInput] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState('');

  const [loadingRoles, setLoadingRoles] = useState(true);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [rolePendingDelete, setRolePendingDelete] = useState(null);

  const fetchRoles = useCallback(async () => {
    setListError('');
    setLoadingRoles(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/role`, {
        method: 'GET',
        mode: 'cors',
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const items = (Array.isArray(data) ? data : [])
        .map(normalizeRoleItem)
        .filter(Boolean);
      setRoleItems(items);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching roles from API:', err);
      setListError(err.message || 'Failed to load roles.');
      setRoleItems([]);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const onAddRole = async (e) => {
    e.preventDefault();
    const roleName = roleNameInput.trim();
    const roleCode = (roleCodeInput.trim() || roleName);
    setRoleError('');
    if (!roleName) {
      setRoleError('Role name is required.');
      return;
    }
    if (roleItems.some((r) => r.name.toLowerCase() === roleName.toLowerCase())) {
      setRoleError('A role with this name already exists.');
      return;
    }

    setSavingRole(true);
    try {
      const payload = {
        roleName,
        roleCode,
        roleDescription: roleName,
      };

      const response = await fetch(`${API_BASE_URL}/api/role`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }

      alert('Role added successfully.');
      setRoleNameInput('');
      setRoleCodeInput('');
      await fetchRoles();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating role via API:', err);
      setRoleError(err.message || 'Failed to add role.');
    } finally {
      setSavingRole(false);
    }
  };

  const requestDeleteRole = (item) => {
    setRolePendingDelete(item);
  };

  const cancelDeleteRole = () => {
    setRolePendingDelete(null);
  };

  const confirmDeleteRole = async () => {
    const item = rolePendingDelete;
    if (!item) return;

    if (item.id != null) {
      setDeletingId(item.id);
      try {
        const response = await fetch(`${API_BASE_URL}/api/role?id=${item.id}`, {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errText = await response.text().catch(() => '');
          throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error deleting role via API:', err);
        alert(`Failed to delete role: ${err.message || 'Unknown error'}`);
        setDeletingId(null);
        return;
      }
      setDeletingId(null);
    }

    const next = roleItems.filter((r) => r.name !== item.name);
    setRoleItems(next);
    setRolePendingDelete(null);
  };

  const inputClass =
    'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary';

  return (
    <div className="space-y-6 min-w-0">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">User settings</h2>
        
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <form
          onSubmit={onAddRole}
          className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4"
        >
          <h3 className="font-medium text-gray-900">Add role</h3>

          <div className="space-y-2">
            <label htmlFor="roleName" className="block text-sm font-medium text-gray-700">
              Role name <span className="text-red-500">*</span>
            </label>
            <input
              id="roleName"
              type="text"
              value={roleNameInput}
              onChange={(e) => {
                setRoleNameInput(e.target.value);
                setRoleError('');
              }}
              placeholder="e.g. Supervisor"
              autoComplete="off"
              className={inputClass}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="roleCode" className="block text-sm font-medium text-gray-700">
              Role code
            </label>
            <input
              id="roleCode"
              type="text"
              value={roleCodeInput}
              onChange={(e) => {
                setRoleCodeInput(e.target.value);
                setRoleError('');
              }}
              placeholder="Optional — defaults to role name"
              autoComplete="off"
              className={inputClass}
            />
          </div>

          {/* <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-xs font-mono whitespace-pre-wrap break-words">
            {JSON.stringify(
              {
                roleName: roleNameInput.trim() || '(required)',
                roleCode: roleCodeInput.trim() || roleNameInput.trim() || '(required)',
                roleDescription: roleNameInput.trim() || '(required)',
              },
              null,
              2
            )}
          </div> */}

          {roleError && (
            <p className="text-sm text-red-600" role="alert">
              {roleError}
            </p>
          )}

          <button
            type="submit"
            disabled={savingRole}
            className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
              savingRole ? 'bg-teal-600/70 cursor-not-allowed' : 'hover:opacity-90'
            }`}
            style={{ backgroundColor: '#009688' }}
          >
            {savingRole ? 'Saving...' : 'Add'}
          </button>
        </form>

        <div className="lg:col-span-8 min-w-0 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-medium text-gray-900">Registered roles</h3>
            <button
              type="button"
              onClick={() => fetchRoles()}
              disabled={loadingRoles}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60"
            >
              {loadingRoles ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {listError && (
            <p className="text-sm text-red-600" role="alert">
              {listError}
            </p>
          )}

          {loadingRoles && roleItems.length === 0 && !listError ? (
            <p className="text-sm text-gray-500">Loading roles…</p>
          ) : roleItems.length === 0 ? (
            <p className="text-sm text-gray-500">No roles yet. Use the form on the left.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Role name</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700 w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {roleItems.map((item) => (
                    <tr key={`${item.name}-${item.id ?? 'local'}`}>
                      <td className="px-4 py-2 text-gray-900">{item.name}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => requestDeleteRole(item)}
                          disabled={deletingId === item.id}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete role"
                        >
                          {deletingId === item.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {rolePendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={cancelDeleteRole}
            aria-hidden="true"
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete role</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete the role{' '}
              <span className="font-semibold text-gray-900">&quot;{rolePendingDelete.name}&quot;</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={cancelDeleteRole}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmDeleteRole()}
                disabled={rolePendingDelete.id != null && deletingId === rolePendingDelete.id}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60"
              >
                {rolePendingDelete.id != null && deletingId === rolePendingDelete.id
                  ? 'Deleting…'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettings;
